import "./template-editor.scss";
import React, { useMemo, useState, useCallback } from "react";
import {
  Badge,
  Button,
  Card,
  Checkbox,
  Dropdown,
  Empty,
  Flex,
  Input,
  Space,
  Tabs,
  Tag,
  Tooltip,
  Typography,
  message,
  Collapse,
  Spin,
} from "antd";
import { CKEditor } from "@ckeditor/ckeditor5-react";
import {
  Bold,
  ClassicEditor,
  Essentials,
  Heading,
  Italic,
  Link,
  List,
  Paragraph,
  Plugin,
  Underline,
  type Editor,
  type EditorConfig,
} from "ckeditor5";
import {
  TbCheck,
  TbChevronDown,
  TbChevronLeft,
  TbCopy,
  TbDeviceFloppy,
  TbEye,
  TbFidgetSpinner,
  TbFileText,
  TbMail,
  TbPencil,
  TbPlugConnected,
  TbSearch,
  TbTags,
  TbX,
} from "react-icons/tb";
import { populateVariableRegistry, TemplateVariableEditing } from "./template-variable-plugin";
import { VariableMap, Recipients } from "api/template.service";
import { Resource } from "dtos/stage.dto";
import { resolveStageColor, resolveStageIcon, RESOURCE_LABELS } from "components/Stages/constants";
import { ApiService } from "api/api.service";

const FLAT_TOKEN_RE = /\{\{([a-z][a-z0-9_]*)\}\}/g;

const flatTokensToMarkup = (html: string): string =>
  html
    .split(/(<[^>]*>)/)
    .map((part) =>
      part.startsWith("<")
        ? part
        : part.replace(
            FLAT_TOKEN_RE,
            (_match, key: string) => `<span class="tpl-variable" data-variable="${key}">${key}</span>`,
          ),
    )
    .join("");

const isBlankHtml = (html: string): boolean =>
  html
    .replace(/<[^>]*>/g, "")
    .replace(/&nbsp;/g, " ")
    .trim().length === 0;

class BlockEnterDisable extends Plugin {
  public init(): void {
    const editor = this.editor;
    editor.editing.view.document.on(
      "keydown",
      (evt, data) => {
        if (data.keyCode === 13) {
          evt.stop();
          data.preventDefault();
        }
      },
      { priority: "high" },
    );
  }
}

type TriggerOption = {
  label: string;
  value: string;
  icon: React.ReactNode;
  color: { bg: string; border: string; text: string };
  group: Resource;
};

const DATASOURCE_COLOR: Record<string, { bg: string; border: string; text: string }> = {
  APPLICATION: { bg: "#eff6ff", border: "#3b82f6", text: "#1e40af" },
  ORGANISATION: { bg: "#f0fdf4", border: "#22c55e", text: "#166534" },
  LOAN: { bg: "#fefce8", border: "#eab308", text: "#854d0e" },
  TEAM: { bg: "#faf5ff", border: "#a855f7", text: "#4c1d95" },
};

const MONO = "ui-monospace, SFMono-Regular, Menlo, Consolas, monospace";

const ACCENT = {
  base: "#6366f1",
  strong: "#4f46e5",
  soft: "#eef2ff",
  border: "#c7d2fe",
  text: "#3730a3",
};

const BODY_MIN_HEIGHT = 260;
const SIDE_PANEL_WIDTH = 420;

export interface MessageTemplate {
  name: string;
  subject: string;
  body: string;
  triggers: string[];
  recipients: Recipients;
}

export interface TemplateEditorProps {
  value?: MessageTemplate;
  variableMap: VariableMap;
  onSave?: (next: MessageTemplate) => void;
  saving?: boolean;
  onBack: () => void;
  orgCode?: string;
}

type ActiveTarget = "body" | "to" | "cc" | "subject";

const FieldLabel: React.FC<{ children: React.ReactNode; required?: boolean }> = ({ children, required }) => (
  <Typography.Text
    style={{
      display: "block",
      marginBottom: 6,
      fontSize: 11,
      fontWeight: 600,
      letterSpacing: "0.06em",
      textTransform: "uppercase",
      color: "#6b7280",
    }}
  >
    {children}
    {required ? <span style={{ color: "#ef4444", marginLeft: 3 }}>*</span> : null}
  </Typography.Text>
);

const SectionCard: React.FC<{
  title: string;
  icon: React.ReactNode;
  hint?: React.ReactNode;
  children: React.ReactNode;
}> = ({ title, icon, hint, children }) => (
  <Card
    variant="outlined"
    styles={{ header: { borderBottom: "1px solid #f0f0f0", minHeight: 48 }, body: { padding: 20 } }}
    style={{ borderRadius: 12 }}
    title={
      <Flex align="center" justify="space-between" gap={12}>
        <Space size={8}>
          <span style={{ color: ACCENT.base, display: "flex", fontSize: 16 }}>{icon}</span>
          <Typography.Text strong style={{ fontSize: 13 }}>
            {title}
          </Typography.Text>
        </Space>
        {hint}
      </Flex>
    }
  >
    {children}
  </Card>
);

const EmailTagInput: React.FC<{
  label: string;
  value: string[];
  onChange: (next: string[]) => void;
  placeholder?: string;
  onFocus?: () => void;
}> = ({ label, value, onChange, placeholder, onFocus }) => {
  const [input, setInput] = useState("");
  const [isFocused, setIsFocused] = useState(false);

  const add = useCallback(() => {
    const trimmed = input.trim();
    if (!trimmed) {
      setInput("");
      return;
    }
    if (value.includes(trimmed)) {
      setInput("");
      return;
    }
    onChange([...value, trimmed]);
    setInput("");
  }, [input, value, onChange]);

  const remove = useCallback((item: string) => onChange(value.filter((e) => e !== item)), [value, onChange]);

  return (
    <div
      style={{
        border: `1px solid ${isFocused ? ACCENT.base : "#d9d9d9"}`,
        borderRadius: 8,
        padding: "6px 10px",
        display: "flex",
        flexWrap: "wrap",
        gap: 6,
        alignItems: "center",
        background: "#fff",
        minHeight: 42,
        transition: "border-color 0.2s, box-shadow 0.2s",
        boxShadow: isFocused ? `0 0 0 3px rgba(99,102,241,0.12)` : "none",
        cursor: "text",
      }}
      onClick={() => (document.getElementById(`email-input-${label}`) as HTMLInputElement)?.focus()}
    >
      <Typography.Text type="secondary" style={{ fontSize: 12, marginRight: 4, whiteSpace: "nowrap", fontWeight: 500 }}>
        {label}
      </Typography.Text>
      {value.map((item) => {
        const isVariable = item.startsWith("{{") && item.endsWith("}}");
        return (
          <Tag
            key={item}
            closable
            onClose={() => remove(item)}
            style={{
              marginInlineEnd: 0,
              background: isVariable ? ACCENT.soft : "#f3f4f6",
              border: `1px solid ${isVariable ? ACCENT.border : "#e5e7eb"}`,
              color: isVariable ? ACCENT.text : "#374151",
              borderRadius: 4,
              padding: "2px 8px",
              fontSize: 12,
              fontFamily: isVariable ? MONO : undefined,
            }}
            closeIcon={<TbX size={11} />}
          >
            {isVariable ? item.slice(2, -2) : item}
          </Tag>
        );
      })}
      <input
        id={`email-input-${label}`}
        value={input}
        onChange={(e) => setInput(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === ",") {
            e.preventDefault();
            add();
          }
          if (e.key === "Backspace" && !input && value.length) {
            remove(value[value.length - 1]);
          }
        }}
        onFocus={() => {
          setIsFocused(true);
          onFocus?.();
        }}
        onBlur={() => {
          setIsFocused(false);
          if (input.trim()) add();
        }}
        placeholder={value.length === 0 ? (placeholder ?? "Type and press Enter") : ""}
        style={{
          border: "none",
          outline: "none",
          fontSize: 13,
          flex: 1,
          minWidth: 160,
          background: "transparent",
          padding: "4px 0",
        }}
      />
    </div>
  );
};

const VariableCard: React.FC<{
  flatKey: string;
  details: VariableMap[string];
  theme: { bg: string; border: string; text: string };
  onInsert: (key: string) => void;
}> = ({ flatKey, details, theme, onInsert }) => {
  const [copied, setCopied] = useState(false);

  const handleCopy = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      navigator.clipboard.writeText(`{{${flatKey}}}`).then(() => {
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      });
    },
    [flatKey],
  );

  return (
    <div
      style={{
        padding: "8px 12px",
        borderRadius: 6,
        background: "#fafafa",
        border: "1px solid #f0f0f0",
        cursor: "pointer",
        transition: "background 0.15s, border-color 0.15s",
      }}
      onClick={() => onInsert(flatKey)}
      onMouseEnter={(e) => {
        e.currentTarget.style.background = ACCENT.soft;
        e.currentTarget.style.borderColor = ACCENT.border;
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.background = "#fafafa";
        e.currentTarget.style.borderColor = "#f0f0f0";
      }}
    >
      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
        <div
          style={{
            width: 20,
            height: 20,
            borderRadius: 4,
            background: theme.bg,
            border: `2px solid ${theme.border}`,
            flexShrink: 0,
          }}
        />
        <div style={{ flex: 1, minWidth: 0 }}>
          <code style={{ fontFamily: MONO, fontSize: 12, fontWeight: 600, color: "#1f2937" }}>{flatKey}</code>
          <div style={{ fontSize: 11, color: "#6b7280", marginTop: 2 }}>{details.description}</div>
        </div>
        <Tooltip title={copied ? "Copied" : "Copy token"}>
          <Button
            type="text"
            size="small"
            icon={copied ? <TbCheck size={13} color="#22c55e" /> : <TbCopy size={13} />}
            onClick={handleCopy}
            style={{ color: "#9ca3af", flexShrink: 0 }}
          />
        </Tooltip>
      </div>
    </div>
  );
};

const TriggerDropdown: React.FC<{
  value: string[];
  onChange: (value: string[]) => void;
  orgCode?: string;
}> = ({ value, onChange, orgCode }) => {
  const [visible, setVisible] = useState(false);
  const [searchText, setSearchText] = useState("");
  const [selectedGroup, setSelectedGroup] = useState<Resource | "all">("all");

  const stagesQuery = ApiService.organisation.useGetAllStages(orgCode);
  const stages = stagesQuery.data ?? [];

  // Build trigger options dynamically from stages
  const triggerOptions = useMemo<TriggerOption[]>(() => {
    return stages
      .filter((s) => !s.archived)
      .map((stage) => {
        const Icon = resolveStageIcon(stage.icon);
        const swatch = resolveStageColor(stage.color);
        return {
          label: stage.label,
          value: `${stage.resource}:${stage.value}`,
          icon: (
            <span
              style={{
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                width: 20,
                height: 20,
                borderRadius: 4,
                background: swatch.bg,
                color: swatch.text,
                boxShadow: `inset 0 0 0 1px ${swatch.border}`,
                flexShrink: 0,
              }}
            >
              <Icon size={12} />
            </span>
          ),
          color: swatch,
          group: stage.resource as Resource,
        };
      });
  }, [stages]);

  // Build trigger groups dynamically from available resources in stages
  const triggerGroups = useMemo<Record<Resource, { label: string }>>(
    () =>
      Object.values(Resource).reduce(
        (acc, resource) => {
          acc[resource] = { label: RESOURCE_LABELS[resource] ?? resource };
          return acc;
        },
        {} as Record<Resource, { label: string }>,
      ),
    [],
  );

  const selectedCount = value.length;
  const allSelected = selectedCount === triggerOptions.length;

  const filteredOptions = useMemo(() => {
    const needle = searchText.trim().toLowerCase();
    return triggerOptions.filter(
      (option) =>
        (!needle || option.label.toLowerCase().includes(needle) || option.value.toLowerCase().includes(needle)) &&
        (selectedGroup === "all" || option.group === selectedGroup),
    );
  }, [triggerOptions, searchText, selectedGroup]);

  const groupedOptions = useMemo(() => {
    const map = new Map<Resource, TriggerOption[]>();
    filteredOptions.forEach((option) => {
      if (!map.has(option.group)) map.set(option.group, []);
      map.get(option.group)!.push(option);
    });
    return map;
  }, [filteredOptions]);

  const getGroupStats = (group: Resource) => {
    const groupOptions = triggerOptions.filter((opt) => opt.group === group);
    return {
      total: groupOptions.length,
      selected: groupOptions.filter((opt) => value.includes(opt.value)).length,
    };
  };

  const handleToggle = (triggerValue: string) => {
    onChange(value.includes(triggerValue) ? value.filter((v) => v !== triggerValue) : [...value, triggerValue]);
  };

  const handleSelectAll = () => {
    onChange(allSelected ? [] : triggerOptions.map((opt) => opt.value));
  };

  const handleGroupSelectAll = (group: Resource) => {
    const groupValues = triggerOptions.filter((opt) => opt.group === group).map((opt) => opt.value);
    const allSelectedInGroup = groupValues.every((v) => value.includes(v));
    onChange(
      allSelectedInGroup
        ? value.filter((v) => !groupValues.includes(v))
        : [...value, ...groupValues.filter((v) => !value.includes(v))],
    );
  };

  const availableResources = useMemo(
    () => [...new Set(triggerOptions.map((o) => o.group))] as Resource[],
    [triggerOptions],
  );

  const triggerMenu = (
    <div
      style={{
        width: 360,
        padding: 12,
        background: "#fff",
        borderRadius: 8,
        boxShadow: "0 4px 20px rgba(0,0,0,0.15)",
        maxHeight: 480,
        display: "flex",
        flexDirection: "column",
      }}
    >
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: 12,
          flexShrink: 0,
        }}
      >
        <Space size={8}>
          <TbTags />
          <Typography.Text strong style={{ fontSize: 14 }}>
            Select triggers
          </Typography.Text>
        </Space>
        <Space size={4}>
          <Button type="text" size="small" onClick={handleSelectAll} style={{ fontSize: 12 }}>
            {allSelected ? "Deselect all" : "Select all"}
          </Button>
          <Button
            type="text"
            size="small"
            onClick={() => {
              onChange([]);
              setVisible(false);
            }}
            style={{ fontSize: 12, color: "#ef4444" }}
          >
            Clear
          </Button>
        </Space>
      </div>

      <Input
        size="small"
        placeholder="Search triggers"
        prefix={<TbSearch color="#9ca3af" />}
        value={searchText}
        onChange={(e) => setSearchText(e.target.value)}
        style={{ marginBottom: 12, borderRadius: 6 }}
        allowClear
      />

      {/* Resource filter tabs */}
      <div style={{ display: "flex", gap: 6, marginBottom: 12, flexWrap: "wrap", flexShrink: 0 }}>
        <Button
          size="small"
          type={selectedGroup === "all" ? "primary" : "default"}
          onClick={() => setSelectedGroup("all")}
          style={{ borderRadius: 4 }}
        >
          All
        </Button>
        {availableResources.map((resource) => {
          const stats = getGroupStats(resource);
          return (
            <Badge
              key={resource}
              count={stats.selected}
              size="small"
              style={{ backgroundColor: stats.selected > 0 ? ACCENT.base : undefined, fontSize: 9 }}
            >
              <Button
                size="small"
                type={selectedGroup === resource ? "primary" : "default"}
                onClick={() => setSelectedGroup(resource)}
                style={{ borderRadius: 4 }}
              >
                {triggerGroups[resource].label}
              </Button>
            </Badge>
          );
        })}
      </div>

      {selectedCount > 0 && (
        <div
          style={{
            padding: "6px 10px",
            background: ACCENT.soft,
            borderRadius: 6,
            marginBottom: 8,
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            flexShrink: 0,
          }}
        >
          <Typography.Text style={{ fontSize: 12, color: ACCENT.strong }}>{selectedCount} selected</Typography.Text>
          <Button
            type="link"
            size="small"
            onClick={() => onChange([])}
            style={{ fontSize: 11, padding: 0, height: "auto" }}
          >
            Clear all
          </Button>
        </div>
      )}

      <div style={{ flex: 1, overflowY: "auto", marginTop: 4 }}>
        {stagesQuery.isLoading ? (
          <Flex justify="center" style={{ padding: 32 }}>
            <Spin size="small" />
          </Flex>
        ) : groupedOptions.size === 0 ? (
          <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No triggers found" style={{ margin: "16px 0" }} />
        ) : (
          <Space direction="vertical" size={8} style={{ width: "100%" }}>
            {[...groupedOptions.entries()].map(([resource, options]) => {
              const stats = getGroupStats(resource);
              const allSelectedInGroup = stats.selected === stats.total && stats.total > 0;

              return (
                <div key={resource}>
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      padding: "4px 8px",
                      marginBottom: 4,
                      background: "#f9fafb",
                      borderRadius: 4,
                    }}
                  >
                    <Space size={6}>
                      <Typography.Text strong style={{ fontSize: 12 }}>
                        {triggerGroups[resource].label}
                      </Typography.Text>
                      <Tag style={{ fontSize: 10, margin: 0 }}>
                        {stats.selected}/{stats.total}
                      </Tag>
                    </Space>
                    <Button
                      type="link"
                      size="small"
                      onClick={() => handleGroupSelectAll(resource)}
                      style={{ fontSize: 11, padding: "0 4px", height: "auto" }}
                    >
                      {allSelectedInGroup ? "Deselect all" : "Select all"}
                    </Button>
                  </div>

                  <Space direction="vertical" size={2} style={{ width: "100%" }}>
                    {options.map((option) => {
                      const isSelected = value.includes(option.value);
                      return (
                        <div
                          key={option.value}
                          style={{
                            padding: "6px 10px",
                            borderRadius: 6,
                            cursor: "pointer",
                            display: "flex",
                            alignItems: "center",
                            gap: 8,
                            background: isSelected ? ACCENT.soft : "transparent",
                            transition: "background 0.15s",
                          }}
                          onClick={() => handleToggle(option.value)}
                          onMouseEnter={(e) => {
                            if (!isSelected) e.currentTarget.style.background = "#f9fafb";
                          }}
                          onMouseLeave={(e) => {
                            if (!isSelected) e.currentTarget.style.background = "transparent";
                          }}
                        >
                          <Checkbox
                            checked={isSelected}
                            onChange={() => handleToggle(option.value)}
                            style={{ flexShrink: 0 }}
                          />
                          {option.icon}
                          <Typography.Text style={{ fontSize: 13, flex: 1 }}>{option.label}</Typography.Text>
                        </div>
                      );
                    })}
                  </Space>
                </div>
              );
            })}
          </Space>
        )}
      </div>
    </div>
  );

  return (
    <Dropdown
      open={visible}
      onOpenChange={setVisible}
      popupRender={() => triggerMenu}
      trigger={["click"]}
      placement="topLeft"
    >
      <Button
        style={{
          borderRadius: 4,
          display: "flex",
          alignItems: "center",
          gap: 6,
          borderColor: selectedCount > 0 ? ACCENT.base : undefined,
          color: selectedCount > 0 ? ACCENT.strong : undefined,
        }}
      >
        <TbFidgetSpinner />
        <span>Triggers</span>
        {selectedCount > 0 && (
          <Badge count={selectedCount} style={{ backgroundColor: ACCENT.base, fontSize: 10, marginLeft: 4 }} />
        )}
        <TbChevronDown size={12} />
      </Button>
    </Dropdown>
  );
};

const TemplateEditor: React.FC<TemplateEditorProps> = ({
  value,
  variableMap,
  onSave,
  saving = false,
  onBack,
  orgCode,
}) => {
  const [bodyEditor, setBodyEditor] = useState<Editor | null>(null);
  const [subjectEditor, setSubjectEditor] = useState<Editor | null>(null);
  const [name, setName] = useState(value?.name ?? "");
  const [body, setBody] = useState(value?.body ?? "");
  const [subjectFilled, setSubjectFilled] = useState(() => !isBlankHtml(value?.subject ?? ""));
  const [triggers, setTriggers] = useState<string[]>(value?.triggers ?? []);
  const [to, setTo] = useState<string[]>(value?.recipients?.to ?? []);
  const [cc, setCc] = useState<string[]>(value?.recipients?.cc ?? []);
  const [search, setSearch] = useState("");
  const [activeTarget, setActiveTarget] = useState<ActiveTarget>("body");

  useMemo(() => {
    populateVariableRegistry(variableMap);
  }, [variableMap]);

  const initialSubject = useMemo(
    () => flatTokensToMarkup(value?.subject ?? ""),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  const bodyConfig = useMemo<EditorConfig>(
    () => ({
      licenseKey: "GPL",
      plugins: [Essentials, Paragraph, Heading, Bold, Italic, Underline, Link, List, TemplateVariableEditing],
      toolbar: [
        "heading",
        "|",
        "bold",
        "italic",
        "underline",
        "link",
        "bulletedList",
        "numberedList",
        "|",
        "undo",
        "redo",
      ],
    }),
    [],
  );

  const subjectConfig = useMemo<EditorConfig>(
    () => ({
      licenseKey: "GPL",
      plugins: [Essentials, Paragraph, TemplateVariableEditing, BlockEnterDisable],
      toolbar: [],
    }),
    [],
  );

  // CKEditor sizes the editable to its content, so min-height has to be applied to
  // the editing root rather than the wrapper element.
  const handleBodyReady = useCallback((instance: Editor) => {
    setBodyEditor(instance);
    const root = instance.editing.view.document.getRoot();
    if (!root) return;
    instance.editing.view.change((writer) => {
      writer.setStyle("min-height", `${BODY_MIN_HEIGHT}px`, root);
    });
  }, []);

  const grouped = useMemo(() => {
    const needle = search.trim().toLowerCase();
    const groups = new Map<string, Array<{ flatKey: string; details: VariableMap[string] }>>();
    for (const [flatKey, details] of Object.entries(variableMap)) {
      if (
        needle &&
        !flatKey.includes(needle) &&
        !details.description.toLowerCase().includes(needle) &&
        !details.datasource.toLowerCase().includes(needle)
      )
        continue;
      const source = details.datasource;
      if (!groups.has(source)) groups.set(source, []);
      groups.get(source)!.push({ flatKey, details });
    }
    return Array.from(groups.entries()).sort((a, b) => a[0].localeCompare(b[0]));
  }, [variableMap, search]);

  const totalVariables = useMemo(() => grouped.reduce((sum, [, list]) => sum + list.length, 0), [grouped]);

  const insert = useCallback(
    (flatKey: string) => {
      if (activeTarget === "to") {
        setTo((prev) => (prev.includes(`{{${flatKey}}}`) ? prev : [...prev, `{{${flatKey}}}`]));
        return;
      }
      if (activeTarget === "cc") {
        setCc((prev) => (prev.includes(`{{${flatKey}}}`) ? prev : [...prev, `{{${flatKey}}}`]));
        return;
      }
      if (activeTarget === "body") {
        if (!bodyEditor) {
          message.warning("Editor not ready");
          return;
        }
        bodyEditor.execute("insertTemplateVariable", flatKey);
        bodyEditor.editing.view.focus();
        return;
      }
      if (!subjectEditor) return;
      subjectEditor.execute("insertTemplateVariable", flatKey);
      subjectEditor.editing.view.focus();
    },
    [activeTarget, bodyEditor, subjectEditor],
  );

  const canSave = name.trim().length > 0 && subjectFilled;

  const getSubject = (): string => (subjectEditor ? subjectEditor.getData() : (value?.subject ?? ""));

  const targetColor: Record<ActiveTarget, string> = {
    body: "geekblue",
    to: "green",
    cc: "purple",
    subject: "orange",
  };

  const variablesPanel = (
    <Space direction="vertical" size={10} style={{ width: "100%" }}>
      <div>
        <Input
          allowClear
          size="middle"
          prefix={<TbSearch color="#9ca3af" />}
          placeholder="Search variables"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          style={{ borderRadius: 8 }}
        />
        <div style={{ marginTop: 6, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <Typography.Text type="secondary" style={{ fontSize: 11 }}>
            {totalVariables} variables · click to insert into the active field
          </Typography.Text>
          <Tag color={targetColor[activeTarget]} style={{ fontSize: 10, marginInlineEnd: 0 }}>
            → {activeTarget}
          </Tag>
        </div>
      </div>

      {grouped.length === 0 ? (
        <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No matches" />
      ) : (
        <Collapse
          size="small"
          ghost
          accordion
          defaultActiveKey={grouped.length > 0 ? [grouped[0][0]] : []}
          expandIconPosition="end"
          items={grouped.map(([source, list]) => {
            const theme = DATASOURCE_COLOR[source] ?? { bg: "#f5f5f5", border: "#d9d9d9", text: "#6b7280" };
            return {
              key: source,
              label: (
                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  <div
                    style={{
                      width: 14,
                      height: 14,
                      borderRadius: 3,
                      background: theme.bg,
                      border: `2px solid ${theme.border}`,
                      flexShrink: 0,
                    }}
                  />
                  <Typography.Text strong style={{ fontSize: 12 }}>
                    {source}
                  </Typography.Text>
                  <Badge count={list.length} style={{ backgroundColor: "#e5e7eb", color: "#6b7280", fontSize: 10 }} />
                </div>
              ),
              children: (
                <Space direction="vertical" size={4} style={{ width: "100%" }}>
                  {list.map(({ flatKey, details }) => (
                    <VariableCard key={flatKey} flatKey={flatKey} details={details} theme={theme} onInsert={insert} />
                  ))}
                </Space>
              ),
            };
          })}
        />
      )}
    </Space>
  );

  const previewPanel = (
    <div>
      <div
        style={{
          background: "#fff",
          borderRadius: 8,
          padding: 16,
          minHeight: 320,
          fontSize: 14,
          lineHeight: 1.8,
          border: "1px solid #f0f0f0",
          overflowY: "auto",
        }}
        dangerouslySetInnerHTML={{
          __html: body || "<p style='color:#d1d5db;text-align:center;margin-top:40px'>Nothing to preview yet.</p>",
        }}
      />
      <Typography.Text type="secondary" style={{ fontSize: 11, marginTop: 8, display: "block" }}>
        Variables show as tokens here and are replaced with real values on send.
      </Typography.Text>
    </div>
  );

  return (
    <Flex style={{ height: "calc(100vh - 50px)", overflow: "hidden", background: "#f7f8fa" }}>
      <Flex vertical flex={1} style={{ minWidth: 0 }}>
        <div style={{ flex: 1, overflowY: "auto", padding: 20 }}>
          <Space direction="vertical" size={16} style={{ width: "100%" }}>
            <SectionCard title="Details" icon={<TbPencil />}>
              <Space direction="vertical" size={16} style={{ width: "100%" }}>
                <div>
                  <FieldLabel required>Template name</FieldLabel>
                  <Input
                    value={name}
                    placeholder="e.g. Welcome email"
                    onChange={(e) => setName(e.target.value)}
                    size="large"
                    style={{ borderRadius: 8 }}
                    prefix={<TbPencil color="#9ca3af" />}
                  />
                </div>

                <div>
                  <FieldLabel>
                    <Space size={6}>
                      <TbMail style={{ verticalAlign: "-2px" }} /> Recipients
                    </Space>
                  </FieldLabel>
                  <Space direction="vertical" size={6} style={{ width: "100%" }}>
                    <EmailTagInput
                      label="To"
                      value={to}
                      onChange={setTo}
                      placeholder="Email or {{variable}} — press Enter to add"
                      onFocus={() => setActiveTarget("to")}
                    />
                    <EmailTagInput
                      label="Cc"
                      value={cc}
                      onChange={setCc}
                      placeholder="Optional"
                      onFocus={() => setActiveTarget("cc")}
                    />
                  </Space>
                </div>
              </Space>
            </SectionCard>

            <SectionCard
              title="Message"
              icon={<TbMail />}
              hint={
                <Typography.Text type="secondary" className="inline text-xs opacity-80">
                  <TbPlugConnected style={{ verticalAlign: "-2px", marginRight: 4 }} className="inline" />
                  Click a variable on the right to insert it here
                </Typography.Text>
              }
            >
              <Space direction="vertical" size={16} style={{ width: "100%" }}>
                <div>
                  <FieldLabel required>Subject</FieldLabel>
                  <div
                    className="tpl-subject-host"
                    onFocus={() => setActiveTarget("subject")}
                    style={{
                      border: "1px solid #d9d9d9",
                      borderRadius: 8,
                      overflow: "hidden",
                      cursor: "text",
                    }}
                  >
                    <CKEditor
                      editor={ClassicEditor}
                      config={subjectConfig}
                      data={initialSubject}
                      onReady={(instance) => setSubjectEditor(instance)}
                      onChange={(_e, instance) => setSubjectFilled(!isBlankHtml(instance.getData()))}
                    />
                  </div>
                </div>

                <div>
                  <FieldLabel>Body</FieldLabel>
                  <div
                    className="tpl-editor-host"
                    style={{ borderRadius: 8, overflow: "hidden", border: "1px solid #d9d9d9" }}
                    onFocus={() => setActiveTarget("body")}
                  >
                    <CKEditor
                      editor={ClassicEditor}
                      config={bodyConfig}
                      data={value?.body ?? ""}
                      onReady={handleBodyReady}
                      onChange={(_e, instance) => setBody(instance.getData())}
                    />
                  </div>
                </div>
              </Space>
            </SectionCard>
          </Space>
        </div>

        <Flex
          align="center"
          justify="space-between"
          gap={12}
          className="sticky bottom-0"
          style={{
            padding: "12px 20px",
            background: "#fff",
            borderTop: "1px solid #e5e7eb",
            flexShrink: 0,
          }}
        >
          <TriggerDropdown value={triggers} onChange={setTriggers} orgCode={orgCode} />
          <Space size={12}>
            {!canSave && (
              <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                Add a template name and subject to save
              </Typography.Text>
            )}
            <Button
              type="primary"
              icon={<TbDeviceFloppy />}
              loading={saving}
              disabled={!canSave}
              onClick={() =>
                onSave?.({
                  name,
                  subject: getSubject(),
                  body,
                  triggers,
                  recipients: { to, cc },
                })
              }
            >
              Save template
            </Button>
          </Space>
        </Flex>
      </Flex>

      <div
        style={{
          width: SIDE_PANEL_WIDTH,
          flexShrink: 0,
          background: "#fff",
          borderLeft: "1px solid #e5e7eb",
          overflowY: "auto",
        }}
      >
        <Tabs
          className="px-5 py-2"
          size="small"
          defaultActiveKey="variables"
          items={[
            {
              key: "variables",
              label: (
                <Space size={4}>
                  <TbPlugConnected style={{ verticalAlign: "-2px" }} />
                  Variables
                  <Badge
                    count={totalVariables}
                    style={{ backgroundColor: "#e5e7eb", color: "#6b7280", fontSize: 10 }}
                  />
                </Space>
              ),
              children: variablesPanel,
            },
            {
              key: "preview",
              label: (
                <Space size={4}>
                  <TbEye style={{ verticalAlign: "-2px" }} />
                  Preview
                </Space>
              ),
              children: previewPanel,
            },
          ]}
        />
      </div>
    </Flex>
  );
};

export default TemplateEditor;
